/**
 * FormField Component
 * 
 * Reusable form field wrapper with validation display,
 * labels, help text, and consistent styling.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationCircle,
  faInfoCircle,
  faPhone,
  faDollarSign,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { phoneFormatter, currencyFormatter, dateFormatter } from '../../../utils/formatters';
import './FormField.css';

/**
 * FormField wrapper component
 */
const FormField = ({
  label,
  name,
  error,
  touched,
  required = false,
  helpText,
  children,
  className = '',
  showValidIcon = false,
  inline = false
}) => {
  const hasError = touched && error;
  const isValid = touched && !error && showValidIcon;

  return (
    <div className={`form-field ${hasError ? 'has-error' : ''} ${isValid ? 'is-valid' : ''} ${inline ? 'inline' : ''} ${className}`}>
      {label && (
        <label htmlFor={name} className="form-field-label">
          {label}
          {required && <span className="required-mark">*</span>}
        </label>
      )}

      <div className="form-field-input-wrapper">
        {children}
        
        {hasError && (
          <span className="form-field-icon error">
            <FontAwesomeIcon icon={faExclamationCircle} />
          </span>
        )}
        
        {isValid && (
          <span className="form-field-icon valid">
            <FontAwesomeIcon icon={faCheck} />
          </span>
        )}
      </div>

      {hasError && (
        <p className="form-field-error">
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error}
        </p>
      )}

      {helpText && !hasError && (
        <p className="form-field-help">
          <FontAwesomeIcon icon={faInfoCircle} />
          {helpText}
        </p>
      )}
    </div>
  );
};

/**
 * Text Input with FormField styling
 */
export const TextInput = ({
  label,
  name,
  type = 'text',
  error,
  touched,
  isValid,
  required,
  helpText,
  placeholder,
  className = '',
  inputClassName = '',
  showValidIcon,
  ...inputProps
}) => (
  <FormField
    label={label}
    name={name}
    error={error}
    touched={touched}
    required={required}
    helpText={helpText}
    showValidIcon={showValidIcon}
    className={className}
  >
    <input
      type={type}
      id={name}
      name={name}
      className={`form-control ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
      placeholder={placeholder}
      {...inputProps}
    />
  </FormField>
);

/**
 * Textarea with FormField styling
 */
export const TextArea = ({
  label,
  name,
  error,
  touched,
  isValid,
  required,
  helpText,
  placeholder,
  rows = 4,
  className = '',
  inputClassName = '',
  showValidIcon,
  maxLength,
  value,
  ...inputProps
}) => (
  <FormField
    label={label}
    name={name}
    error={error}
    touched={touched}
    required={required}
    helpText={helpText}
    showValidIcon={showValidIcon}
    className={className}
  >
    <textarea
      id={name}
      name={name}
      rows={rows}
      className={`form-control ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
      placeholder={placeholder}
      maxLength={maxLength}
      value={value}
      {...inputProps}
    />
    {maxLength && (
      <span className="form-field-counter">
        {(value?.length || 0)} / {maxLength}
      </span>
    )}
  </FormField>
);

/**
 * Select with FormField styling
 */
export const SelectInput = ({
  label,
  name,
  error,
  touched,
  required,
  helpText,
  options = [],
  placeholder = 'Select an option...',
  className = '',
  inputClassName = '',
  showValidIcon,
  ...inputProps
}) => (
  <FormField
    label={label}
    name={name}
    error={error}
    touched={touched}
    required={required}
    helpText={helpText}
    showValidIcon={showValidIcon}
    className={className}
  >
    <select
      id={name}
      name={name}
      className={`form-select ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
      {...inputProps}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </option>
      ))}
    </select>
  </FormField>
);

/**
 * Checkbox with FormField styling
 */
export const Checkbox = ({
  label,
  name,
  error,
  touched,
  helpText,
  className = '',
  inputClassName = '',
  checked,
  ...inputProps
}) => (
  <div className={`form-field form-field-checkbox ${className}`}>
    <label className="checkbox-label">
      <input
        type="checkbox"
        id={name}
        name={name}
        className={`form-check-input ${inputClassName}`}
        checked={checked}
        {...inputProps}
      />
      <span className="checkbox-text">{label}</span>
    </label>

    {touched && error && (
      <p className="form-field-error">
        <FontAwesomeIcon icon={faExclamationCircle} />
        {error}
      </p>
    )}

    {helpText && !(touched && error) && (
      <p className="form-field-help">
        <FontAwesomeIcon icon={faInfoCircle} />
        {helpText}
      </p>
    )}
  </div>
);

/**
 * Radio Group with FormField styling
 */
export const RadioGroup = ({
  label,
  name,
  options = [],
  error,
  touched,
  required,
  helpText,
  className = '',
  value,
  onChange,
  inline = false
}) => (
  <FormField
    label={label}
    name={name}
    error={error}
    touched={touched}
    required={required}
    helpText={helpText}
    className={`form-field-radio-group ${className}`}
  >
    <div className={`radio-options ${inline ? 'inline' : ''}`}>
      {options.map((option) => (
        <label key={option.value} className="radio-label">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={onChange}
            disabled={option.disabled}
            className="form-check-input"
          />
          <span className="radio-text">{option.label}</span>
        </label>
      ))}
    </div>
  </FormField>
);

/**
 * Phone Input with automatic formatting
 */
export const PhoneInput = ({
  label,
  name,
  error,
  touched,
  required,
  helpText,
  placeholder = '(340) 123-4567',
  className = '',
  inputClassName = '',
  showValidIcon,
  value,
  onChange,
  onBlur,
  ...inputProps
}) => {
  const handleChange = (e) => {
    const formatted = phoneFormatter.format(e.target.value);
    if (onChange) {
      // Create a synthetic event with the formatted value
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name,
          value: formatted
        }
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <FormField
      label={label}
      name={name}
      error={error}
      touched={touched}
      required={required}
      helpText={helpText || 'Format: (XXX) XXX-XXXX'}
      showValidIcon={showValidIcon}
      className={className}
    >
      <div className="input-with-icon">
        <span className="input-icon left">
          <FontAwesomeIcon icon={faPhone} />
        </span>
        <input
          type="tel"
          id={name}
          name={name}
          className={`form-control with-icon-left ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          maxLength={16}
          {...inputProps}
        />
      </div>
    </FormField>
  );
};

/**
 * Currency Input with dollar sign prefix and formatting
 */
export const CurrencyInput = ({
  label,
  name,
  error,
  touched,
  required,
  helpText,
  placeholder = '0.00',
  className = '',
  inputClassName = '',
  showValidIcon,
  value,
  onChange,
  onBlur,
  ...inputProps
}) => {
  const handleChange = (e) => {
    const formatted = currencyFormatter.formatInput(e.target.value);
    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name,
          value: formatted
        }
      };
      onChange(syntheticEvent);
    }
  };

  const handleBlur = (e) => {
    // Format on blur for display
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <FormField
      label={label}
      name={name}
      error={error}
      touched={touched}
      required={required}
      helpText={helpText}
      showValidIcon={showValidIcon}
      className={className}
    >
      <div className="input-with-icon">
        <span className="input-icon left">
          <FontAwesomeIcon icon={faDollarSign} />
        </span>
        <input
          type="text"
          inputMode="decimal"
          id={name}
          name={name}
          className={`form-control with-icon-left ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          {...inputProps}
        />
      </div>
    </FormField>
  );
};

/**
 * Date Input with USVI timezone support
 */
export const DateInput = ({
  label,
  name,
  error,
  touched,
  required,
  helpText,
  className = '',
  inputClassName = '',
  showValidIcon,
  value,
  onChange,
  min,
  max,
  ...inputProps
}) => {
  // Format value for the date input
  const inputValue = value ? dateFormatter.formatForInput(value) : '';

  const handleChange = (e) => {
    if (onChange) {
      const parsed = dateFormatter.parseInput(e.target.value);
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name,
          value: parsed ? parsed.toISOString() : ''
        }
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <FormField
      label={label}
      name={name}
      error={error}
      touched={touched}
      required={required}
      helpText={helpText || 'USVI Time (AST)'}
      showValidIcon={showValidIcon}
      className={className}
    >
      <div className="input-with-icon">
        <span className="input-icon left">
          <FontAwesomeIcon icon={faCalendar} />
        </span>
        <input
          type="date"
          id={name}
          name={name}
          className={`form-control with-icon-left ${inputClassName} ${touched && error ? 'is-invalid' : ''}`}
          value={inputValue}
          onChange={handleChange}
          min={min ? dateFormatter.formatForInput(min) : undefined}
          max={max ? dateFormatter.formatForInput(max) : undefined}
          {...inputProps}
        />
      </div>
    </FormField>
  );
};

export default FormField;
